import React, { useState } from 'react';
import { Alert, AlertIcon, AlertTitle, AlertDescription, Box, Badge } from '@chakra-ui/react';
import { useAuthUser } from 'react-auth-kit';
import { useTranslation } from 'react-i18next';
import useGetGeoScopeForCurrentUser from 'hooks/useGetGeoScopeForCurrentUser';

const UserGeoAccessBanner = () => {
  const { t } = useTranslation('geo_access_banner');
  const auth = useAuthUser();
  const userRoles = auth()?.user?.roles || [];
  const [isExpanded, setIsExpanded] = useState(false);

  const { loadingGeo, geoScope } = useGetGeoScopeForCurrentUser();

  // Don't show banner if loading
  if (loadingGeo) return null;

  const { regions, departments, arrondissements } = geoScope?.data || {};
  const scopeLevel = geoScope?.data?.scopeLevel;
  const hasRestrictions =
    regions?.length > 0 || departments?.length > 0 || arrondissements?.length > 0;

  return (
    <Alert
      status="info"
      variant="subtle"
      className="mb-4 rounded-lg shadow-md transition-all duration-300 cursor-pointer"
      flexDirection="column"
      alignItems="flex-start"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      sx={{
        maxHeight: isExpanded ? '500px' : '40px',
        overflow: 'hidden',
        transition: 'max-height 0.3s ease-in-out',
      }}
    >
      <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <AlertIcon />
          <AlertTitle mr={2}>{t('banner_title') || 'User Info & Access'}</AlertTitle>
        </Box>
        <span className="text-xs text-gray-500">
          {isExpanded
            ? t('hover_to_collapse') || '↑ Collapse'
            : t('hover_to_expand') || '↓ Hover to expand'}
        </span>
      </Box>
      <AlertDescription mt={2} width="100%">
        <div className="flex flex-wrap gap-3 items-center">
          {/* User Roles */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="font-semibold text-orange-700">
              {t('user_roles') || 'User Roles'}:
            </span>
            {userRoles.map((role, index) => (
              <Badge key={index} colorScheme="orange" fontSize="sm">
                {role.name || role}
              </Badge>
            ))}
          </div>

          {/* Geographic Restrictions */}
          {hasRestrictions && (
            <>
              <span className="font-semibold">
                {t('your_access_limited_to') || 'Your access is limited to:'}
              </span>

              {/* Scope Level Badge */}
              {scopeLevel && (
                <div className="flex gap-1 items-center">
                  <span className="text-sm font-medium text-red-700">
                    {t('scope_level') || 'Scope Level'}:
                  </span>
                  <Badge colorScheme="red" fontSize="sm" fontWeight="bold">
                    {scopeLevel}
                  </Badge>
                </div>
              )}

              {regions?.length > 0 && (
                <div className="flex gap-1 items-center flex-wrap">
                  <span className="text-sm font-medium text-blue-700">
                    {t('regions') || 'Regions'}:
                  </span>
                  {regions.map((region) => (
                    <Badge key={region.id} colorScheme="blue" fontSize="sm">
                      {region.name}
                    </Badge>
                  ))}
                </div>
              )}

              {departments?.length > 0 && (
                <div className="flex gap-1 items-center flex-wrap">
                  <span className="text-sm font-medium text-green-700">
                    {t('departments') || 'Departments'}:
                  </span>
                  {departments.map((dept) => (
                    <Badge key={dept.id} colorScheme="green" fontSize="sm">
                      {dept.name}
                    </Badge>
                  ))}
                </div>
              )}

              {arrondissements?.length > 0 && (
                <div className="flex gap-1 items-center flex-wrap">
                  <span className="text-sm font-medium text-purple-700">
                    {t('arrondissements') || 'Arrondissements'}:
                  </span>
                  {arrondissements.map((arr) => (
                    <Badge key={arr.id} colorScheme="purple" fontSize="sm">
                      {arr.name}
                    </Badge>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default UserGeoAccessBanner;
